import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CatalogItem, QuoteFormData } from "@apex/shared";
import { createApiClient, CATALOG_TYPE_SLUGS, asServiceUnavailable, type WidgetApiClient } from "../api/client";
import type { PublicWidgetTheme } from "../api/client";
import type { WidgetConfig, WidgetContextValue, WidgetState, WidgetTheme } from "./types";
import { initialState, resolveWidgetTheme } from "./types";
import { widgetReducer } from "./reducer";
import { useFirstInteraction } from "../hooks/useFirstInteraction";
import { parseUrlHash } from "../hooks/useStatePersistence";

// Context for the API client (separate to avoid re-renders)
const ApiClientContext = createContext<WidgetApiClient | null>(null);

// Context for widget state and actions
const WidgetContext = createContext<WidgetContextValue | null>(null);

interface WidgetProviderProps {
  config: WidgetConfig;
  children: ReactNode;
}

/**
 * Widget Provider
 *
 * Provides state management and API access to the widget component tree.
 */
export function WidgetProvider({ config, children }: WidgetProviderProps) {
  const [state, dispatch] = useReducer(widgetReducer, initialState);

  // Create API client (stable reference)
  const apiClient = useMemo(
    () => createApiClient({ apiUrl: config.apiUrl, apiKey: config.apiKey }),
    [config.apiUrl, config.apiKey]
  );

  // Fetched theme from `GET /api/v1/widget/public-theme`. Starts undefined
  // and is populated on mount; the resolver returns its defaults until then
  // so first paint never blocks on the theme round-trip. Host-supplied
  // `themeConfig` (React/Vue SDK prop) wins over fetched values — SDK
  // consumers can still hand-roll a theme without the round-trip.
  const [fetchedTheme, setFetchedTheme] = useState<WidgetTheme | undefined>(
    undefined,
  );

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getPublicTheme()
      .then((dto) => {
        if (cancelled) return;
        setFetchedTheme(mapPublicTheme(dto));
      })
      .catch(() => {
        // Best-effort — a failed theme fetch must not block widget render.
        // The resolver will produce defaults and the widget still works.
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  // Resolved theme. Merge order: fetched (server) → host-supplied (props),
  // so a host that hand-rolls `themeConfig` keeps full override authority.
  const theme = useMemo(
    () =>
      resolveWidgetTheme({
        ...(fetchedTheme ?? {}),
        ...(config.themeConfig ?? {}),
      }),
    [fetchedTheme, config.themeConfig],
  );

  // Load initial data on mount
  useEffect(() => {
    let cancelled = false;

    async function loadInitialData() {
      dispatch({ type: "INIT" });

      try {
        const rootItemsResponse = await apiClient.getRootItems();

        if (cancelled) return;

        dispatch({
          type: "DATA_LOADED",
          payload: {
            rootItems: rootItemsResponse.items,
            filters: rootItemsResponse.filters,
            categories: rootItemsResponse.categories,
            rootHasPricing: rootItemsResponse.rootHasPricing,
          },
        });

        // If an entry model is specified, auto-select it — UNLESS the widget's own
        // saved/shared state hash (#m=…) is present. That hash is authoritative: it
        // restores the full config (model + accessories) via useStatePersistence, and
        // auto-selecting here would re-apply the model's DEFAULT accessories over the
        // top, clobbering the restored selection (the deep-link ⨯ refresh regression).
        if (config.initialRootItem && !parseUrlHash(window.location.hash)) {
          const initialItemProperty = config.initialItemProperty ?? "code";
          const rootItem = rootItemsResponse.items.find((item) =>
            initialItemProperty === "id"
              ? item.id === config.initialRootItem
              : item.code === config.initialRootItem
          );

          if (rootItem) {
            await loadRootItem(rootItem);
          } else {
            // Not present in the initially loaded list (e.g. it's outside
            // the default page/filter window) — resolve it explicitly via
            // the client instead of silently doing nothing.
            try {
              const resolved =
                initialItemProperty === "code"
                  ? await apiClient.getItemByCode(config.initialRootItem)
                  : await apiClient.getItem(config.initialRootItem);

              if (cancelled) return;

              await loadRootItem(resolved);
            } catch (error) {
              if (cancelled) return;
              const su = asServiceUnavailable(error);
              if (su) {
                dispatch({ type: "SERVICE_UNAVAILABLE", payload: su });
                return;
              }
              dispatch({
                type: "ERROR",
                payload:
                  error instanceof Error
                    ? error
                    : new Error(
                        `Failed to resolve the initial item "${config.initialRootItem}" (by ${initialItemProperty}).`
                      ),
              });
            }
          }
        }
      } catch (error) {
        if (cancelled) return;
        const su = asServiceUnavailable(error);
        if (su) {
          dispatch({ type: "SERVICE_UNAVAILABLE", payload: su });
          return;
        }
        dispatch({
          type: "ERROR",
          payload:
            error instanceof Error ? error : new Error("Failed to load data"),
        });
      }
    }

    async function loadRootItem(rootItem: CatalogItem) {
      dispatch({ type: "SELECT_ROOT_ITEM", payload: rootItem });

      try {
        const response = await apiClient.getCompatibleItems(
          CATALOG_TYPE_SLUGS.CHILD_TYPE_SLUG,
          rootItem.id
        );

        if (cancelled) return;

        dispatch({
          type: "ROOT_ITEM_LOADED",
          payload: {
            rootItem,
            items: response.items,
            rules: response.rules,
            defaultItemIds: response.defaultItemIds,
            categories: response.categories ?? [],
          },
        });

        // If initial child items are specified, find their IDs and set them
        if (config.initialItems?.length) {
          const itemIds = response.items
            .filter((a) => config.initialItems!.includes(a.code))
            .map((a) => a.id);
          if (itemIds.length > 0) {
            dispatch({
              type: "SET_SELECTED_ITEMS",
              payload: itemIds,
            });
          }
        }
      } catch (error) {
        if (cancelled) return;
        const su = asServiceUnavailable(error);
        if (su) {
          dispatch({ type: "SERVICE_UNAVAILABLE", payload: su });
          return;
        }
        dispatch({
          type: "ERROR",
          payload:
            error instanceof Error
              ? error
              : new Error("Failed to load item details"),
        });
      }
    }

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [
    apiClient,
    config.initialRootItem,
    config.initialItemProperty,
    config.initialItems,
  ]);

  // Re-fetch root items when the search term changes (post-mount only).
  // The SearchInput already debounces typing into a single update, so by the
  // time we get here the user has paused. We skip the very first run because
  // the mount effect above already fetches with the empty searchTerm.
  const searchInitFiredRef = useRef(false);
  useEffect(() => {
    if (!searchInitFiredRef.current) {
      searchInitFiredRef.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rootItemsResponse = await apiClient.getRootItems({
          search: state.searchTerm,
        });
        if (cancelled) return;
        dispatch({
          type: "DATA_LOADED",
          payload: {
            rootItems: rootItemsResponse.items,
            filters: rootItemsResponse.filters,
            categories: rootItemsResponse.categories,
            rootHasPricing: rootItemsResponse.rootHasPricing,
          },
        });
      } catch {
        // Silent — search refetch errors don't tank the entire view.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, state.searchTerm]);

  // Action: Select a root item
  const selectRootItem = useCallback(
    async (rootItem: CatalogItem) => {
      dispatch({ type: "SELECT_ROOT_ITEM", payload: rootItem });

      try {
        const response = await apiClient.getCompatibleItems(
          CATALOG_TYPE_SLUGS.CHILD_TYPE_SLUG,
          rootItem.id
        );

        dispatch({
          type: "ROOT_ITEM_LOADED",
          payload: {
            rootItem,
            items: response.items,
            rules: response.rules,
            defaultItemIds: response.defaultItemIds,
            categories: response.categories ?? [],
          },
        });
      } catch (error) {
        const su = asServiceUnavailable(error);
        if (su) {
          dispatch({ type: "SERVICE_UNAVAILABLE", payload: su });
          return;
        }
        dispatch({
          type: "ERROR",
          payload:
            error instanceof Error
              ? error
              : new Error("Failed to load item details"),
        });
      }
    },
    [apiClient]
  );

  // Action: Toggle item selection (by ID)
  const toggleItem = useCallback((id: string) => {
    dispatch({ type: "TOGGLE_ITEM", payload: id });
  }, []);

  // Action: Set selected items (replace all, by ID)
  const setSelectedItems = useCallback((ids: string[]) => {
    dispatch({ type: "SET_SELECTED_ITEMS", payload: ids });
  }, []);

  // Action: Set filter value
  const setFilter = useCallback((key: string, value: string) => {
    dispatch({ type: "SET_FILTER", payload: { key, value } });
  }, []);

  // Action: Set a range filter (min/max)
  const setRangeFilter = useCallback(
    (key: string, range: { min: number; max: number }) => {
      dispatch({ type: "SET_RANGE_FILTER", payload: { key, range } });
    },
    []
  );

  // Action: Clear a single range filter
  const clearRangeFilter = useCallback((key: string) => {
    dispatch({ type: "CLEAR_RANGE_FILTER", payload: key });
  }, []);

  // Action: Clear all filters (selects + ranges + search)
  const clearFilters = useCallback(() => {
    dispatch({ type: "CLEAR_FILTERS" });
  }, []);

  // Action: Set search term
  const setSearchTerm = useCallback((term: string) => {
    dispatch({ type: "SET_SEARCH_TERM", payload: term });
  }, []);

  // Action: Set accessory search term (Configure step)
  const setAccessorySearchTerm = useCallback((term: string) => {
    dispatch({ type: "SET_ACCESSORY_SEARCH_TERM", payload: term });
  }, []);

  // Action: Set sort key
  const setSortKey = useCallback((sortKey: string) => {
    dispatch({ type: "SET_SORT_KEY", payload: sortKey });
  }, []);

  // Action: Update quote form
  const updateQuoteForm = useCallback((data: Partial<QuoteFormData>) => {
    dispatch({ type: "UPDATE_QUOTE_FORM", payload: data });
  }, []);

  // Action: Set the anti-spam honeypot value (#538)
  const setHoneypot = useCallback((value: string) => {
    dispatch({ type: "SET_HONEYPOT", payload: value });
  }, []);

  // Action: View summary
  const viewSummary = useCallback(() => {
    dispatch({ type: "VIEW_SUMMARY" });
  }, []);

  // Anti-spam: track first user interaction (#538). Used by submitQuote
  // to populate firstInteractionAt on the request payload.
  const firstInteractionAt = useFirstInteraction();

  // Synchronous guard against a rapid double-click on the Submit button (B11).
  // The state-only `isSubmitting` flag from the reducer flips on the next
  // render, so two clicks in the same tick both observe `isSubmitting=false`.
  // A ref updates synchronously, so the second call short-circuits before the
  // API ever sees it. Without this, customers got two identical quote rows.
  const submittingRef = useRef(false);

  // Action: Submit quote
  const submitQuote = useCallback(async () => {
    if (submittingRef.current) return;
    if (!state.selectedRootItem) return;

    submittingRef.current = true;
    dispatch({ type: "SUBMIT_QUOTE" });

    try {
      // Map CatalogItem IDs back to codes for the quote API
      const selectedItemCodes = state.items
        .filter((a) => state.selectedItems.has(a.id))
        .map((a) => a.code);

      const result = await apiClient.submitQuote({
        name: state.quoteForm.name || "",
        email: state.quoteForm.email || "",
        phone: state.quoteForm.phone || "",
        county: state.quoteForm.county,
        company: state.quoteForm.company,
        notes: state.quoteForm.notes,
        rootItemCode: state.selectedRootItem.code,
        itemCodes: selectedItemCodes,
        // Anti-spam (#538). Honeypot is tracked on widget state separately
        // from QuoteFormData so it stays out of the public UI form-data shape.
        honeypot: state.honeypot,
        firstInteractionAt,
        submittedAt: Date.now(),
      });

      dispatch({ type: "QUOTE_SUBMITTED", payload: result });
    } catch (error) {
      const su = asServiceUnavailable(error);
      if (su) {
        dispatch({ type: "SERVICE_UNAVAILABLE", payload: su });
        return;
      }
      // Log the raw error for support diagnostics — the customer-facing
      // ErrorView translates "API request failed: 500 …" into a friendly
      // copy (B17), but engineers debugging a regression want the protocol
      // detail in the console transcript.
      console.error("[ApexWidget] submitQuote failed", error);
      dispatch({
        type: "QUOTE_SUBMITTED",
        payload: {
          success: false,
          message:
            error instanceof Error ? error.message : "Quote submission failed",
        },
      });
    } finally {
      submittingRef.current = false;
    }
  }, [
    apiClient,
    state.selectedRootItem,
    state.quoteForm,
    state.selectedItems,
    state.items,
    firstInteractionAt,
  ]);

  // Action: Go back
  const goBack = useCallback(() => {
    switch (state.view) {
      case "configure":
        dispatch({ type: "BACK_TO_SELECTION" });
        break;
      case "summary":
      case "error":
        dispatch({ type: "BACK_TO_CONFIGURE" });
        break;
      case "success":
        dispatch({ type: "RESET" });
        break;
    }
  }, [state.view]);

  // Action: Reset to initial state
  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // Item navigation: CatalogItemList registers its scroll handler
  const scrollToItemRef = useRef<(id: string) => void>(() => {});

  const scrollToItem = useCallback((id: string) => {
    scrollToItemRef.current(id);
  }, []);

  const registerScrollToItem = useCallback((fn: (id: string) => void) => {
    scrollToItemRef.current = fn;
  }, []);

  // Computed: Total price
  const totalPrice = useMemo(() => {
    const basePrice = state.selectedRootItem?.priceNet ?? 0;
    const itemsPrice = state.items
      .filter((a) => state.selectedItems.has(a.id))
      .reduce((sum, a) => sum + (a.priceNet ?? 0), 0);

    return basePrice + itemsPrice;
  }, [state.selectedRootItem, state.items, state.selectedItems]);

  // Computed: Selected items as array
  const selectedItemList = useMemo(() => {
    return state.items.filter((a) =>
      state.selectedItems.has(a.id)
    );
  }, [state.items, state.selectedItems]);

  // Computed: Filtered root items (select filters + range filters + search,
  // then sorted by the active sort key — search is also applied client-side
  // here so the spinner doesn't flash for tenants whose results fit in one
  // round-trip; the same `searchTerm` is forwarded to the API for tenants
  // with a large catalog.)
  const filteredRootItems = useMemo(() => {
    let items = state.rootItems;

    // Select filters. Metadata cells may carry multi-valued strings — CPL's
    // KL32 stores `basket` as "2 Man, 1 Man" — so we tokenise on commas and
    // match the user's choice against any token. Single-valued cells fall out
    // as a one-element token list, so this remains correct for the common
    // case. (B1) Long-term fix is to type metadata as `string | string[]` on
    // the wire and stop encoding arrays as strings; see Task 4 follow-up.
    if (Object.keys(state.activeFilters).length > 0) {
      items = items.filter((rootItem) =>
        Object.entries(state.activeFilters).every(([key, value]) => {
          if (!value) return true;
          const raw = rootItem.metadata?.[key];
          if (raw == null || raw === "") return false;
          const tokens = String(raw).split(",").map((s) => s.trim());
          return tokens.includes(value);
        })
      );
    }

    // Range filters — parse the metadata value as a number, then bounds check.
    if (Object.keys(state.activeRangeFilters).length > 0) {
      items = items.filter((rootItem) =>
        Object.entries(state.activeRangeFilters).every(([key, range]) => {
          const raw = rootItem.metadata?.[key];
          if (raw == null || raw === "") return false;
          const num = Number(raw);
          if (Number.isNaN(num)) return false;
          return num >= range.min && num <= range.max;
        })
      );
    }

    // Free-text search (case-insensitive over name + code + description)
    const term = state.searchTerm.trim().toLowerCase();
    if (term.length > 0) {
      items = items.filter((rootItem) => {
        const haystack = [
          rootItem.name ?? "",
          rootItem.code,
          rootItem.description ?? "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    }

    // Sort
    const sortKey = state.sortKey || "featured";
    const sorted = [...items];
    if (sortKey === "name-asc") {
      sorted.sort((a, b) => (a.name ?? a.code).localeCompare(b.name ?? b.code));
    } else if (sortKey === "name-desc") {
      sorted.sort((a, b) => (b.name ?? b.code).localeCompare(a.name ?? a.code));
    } else if (sortKey.startsWith("range:")) {
      // range:{filterKey}:asc|desc
      const parts = sortKey.split(":");
      const metaKey = parts[1];
      const direction = parts[2] === "desc" ? -1 : 1;
      sorted.sort((a, b) => {
        const av = Number(a.metadata?.[metaKey] ?? NaN);
        const bv = Number(b.metadata?.[metaKey] ?? NaN);
        const aNaN = Number.isNaN(av);
        const bNaN = Number.isNaN(bv);
        if (aNaN && bNaN) return 0;
        if (aNaN) return 1;
        if (bNaN) return -1;
        return (av - bv) * direction;
      });
    } else {
      // "featured" / fallback — preserve displayOrder
      sorted.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    return sorted;
  }, [
    state.rootItems,
    state.activeFilters,
    state.activeRangeFilters,
    state.searchTerm,
    state.sortKey,
  ]);

  // Context value
  const contextValue: WidgetContextValue = useMemo(
    () => ({
      state,
      dispatch,
      theme,
      selectRootItem,
      toggleItem,
      setSelectedItems,
      setFilter,
      setRangeFilter,
      clearRangeFilter,
      clearFilters,
      setSearchTerm,
      setAccessorySearchTerm,
      setSortKey,
      updateQuoteForm,
      setHoneypot,
      viewSummary,
      submitQuote,
      goBack,
      reset,
      totalPrice,
      selectedItemList,
      filteredRootItems,
      scrollToItem,
      registerScrollToItem,
    }),
    [
      state,
      theme,
      selectRootItem,
      toggleItem,
      setSelectedItems,
      setFilter,
      setRangeFilter,
      clearRangeFilter,
      clearFilters,
      setSearchTerm,
      setAccessorySearchTerm,
      setSortKey,
      updateQuoteForm,
      setHoneypot,
      viewSummary,
      submitQuote,
      goBack,
      reset,
      totalPrice,
      selectedItemList,
      filteredRootItems,
      scrollToItem,
      registerScrollToItem,
    ]
  );

  return (
    <ApiClientContext.Provider value={apiClient}>
      <WidgetContext.Provider value={contextValue}>
        {children}
      </WidgetContext.Provider>
    </ApiClientContext.Provider>
  );
}

/**
 * Hook to access widget state and actions
 */
export function useWidget(): WidgetContextValue {
  const context = useContext(WidgetContext);

  if (!context) {
    throw new Error("useWidget must be used within a WidgetProvider");
  }

  return context;
}

/**
 * Hook to access the API client directly
 */
export function useApiClient(): WidgetApiClient {
  const client = useContext(ApiClientContext);

  if (!client) {
    throw new Error("useApiClient must be used within a WidgetProvider");
  }

  return client;
}

/**
 * Selector hook for specific state slices (performance optimization)
 */
export function useWidgetSelector<T>(selector: (state: WidgetState) => T): T {
  const { state } = useWidget();
  return selector(state);
}

/**
 * Project a server-fetched `PublicWidgetTheme` onto the partial `WidgetTheme`
 * shape that `resolveWidgetTheme` consumes. Skips the styling/colour fields
 * — those are applied via shadow-DOM CSS variables by the custom element,
 * not by the React tree — and keeps only the behaviour/copy/branding flags
 * the React components read.
 *
 * Null-coalescing rules: empty strings on the server are treated the same
 * as null so a "saved but blank" admin form doesn't override a defaults-
 * supplying client.
 */
function mapPublicTheme(dto: PublicWidgetTheme): WidgetTheme {
  return {
    requireCompany: dto.requireCompany,
    requireExplicitConsent: dto.requireExplicitConsent,
    confirmationCopy:
      dto.confirmationCopy && dto.confirmationCopy.length > 0
        ? dto.confirmationCopy
        : undefined,
    tenantWebsiteUrl: dto.tenantWebsiteUrl ?? null,
    logoUrl: dto.logoUrl ?? null,
  };
}
